import { Injectable, inject } from '@angular/core';
import {ApiDeviceHistoryEntryResponseDto, DevicesApiService} from '@birco/rainmesh-api';
import {HistoryDataViewModel, HistoryGroupViewModel} from '../../features/devices/models/history-view-model.interface';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  Observable,
  of,
  pairwise,
  startWith,
  Subscription,
  switchMap
} from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class HistoryService {
  private devicesApiService = inject(DevicesApiService);

  private subscription = new Subscription();

  private filter$$ = new BehaviorSubject<HistoryFilter>({
    deviceId: undefined,
    size: 20,
    page: 0,
    reloadToken: 0
  });
  private loading$$ = new BehaviorSubject<boolean>(false);
  private historyList$$ = new BehaviorSubject<ApiDeviceHistoryEntryResponseDto[]>([]);
  private canLoadMore$$ = new BehaviorSubject<boolean>(false);

  constructor() {
    this.subscription.add(this.initDataSubscription());
  }

  getHistoryData$(): Observable<HistoryDataViewModel> {
    return combineLatest([
      this.historyList$$,
      this.canLoadMore$$,
      this.loading$$
    ]).pipe(
      map(([data, canLoadMore, loading]) => ({
        data: this.groupByDate(data),
        loading: loading,
        canLoadMore: canLoadMore
      })),
    );
  }

  getLoading$(): Observable<boolean> {
    return this.loading$$.asObservable();
  }

  loadInitial(deviceId: string) {
    this.clearHistoryState();
    const current = this.filter$$.getValue();
    this.filter$$.next({
      ...current,
      deviceId,
      page: 0,
      reloadToken: current.reloadToken + 1
    });
  }

  loadMore(deviceId: string) {
    const current = this.filter$$.getValue();
    if (current.deviceId === deviceId) {
      this.filter$$.next({...current, page: current.page + 1});
    } else {
      this.loadInitial(deviceId);
    }
  }

  reset() {
    this.clearHistoryState();
    const current = this.filter$$.getValue();
    this.filter$$.next({
      ...current,
      deviceId: undefined,
      page: 0,
      reloadToken: current.reloadToken + 1
    });
  }

  private initDataSubscription(): Subscription {
    return this.filter$$.pipe(
      filter(filter => !!filter),
      distinctUntilChanged((a, b) =>
        a.deviceId === b.deviceId && a.page === b.page && a.reloadToken === b.reloadToken
      ),
      startWith(null as any),
      pairwise(),
      switchMap(([prev, curr]) => {
        if (curr.deviceId) {
          this.loading$$.next(true);

          return this.devicesApiService.getDeviceHistory(
            curr.deviceId,
            curr.size,
            curr.page
          ).pipe(
            catchError(error => {
              console.error('Fehler beim Laden der Daten:', error);
              return of(null);
            }),
            finalize(() => {
              this.loading$$.next(false);
            }),
            map(data => ({
              data,
              replace: !prev || prev.deviceId !== curr.deviceId
            }))
          );
        } else {
          return of({data: null, replace: true});
        }
      })
    ).subscribe(({data, replace}) => {
      if (data) {
        this.canLoadMore$$.next(data.history.length >= this.filter$$.getValue().size);
        if (replace) {
          this.historyList$$.next(data.history);
        } else {
          const current = this.historyList$$.getValue();
          this.historyList$$.next([...current, ...data.history]);
        }
      } else {
        this.clearHistoryState();
      }
    });
  }

  private clearHistoryState() {
    this.canLoadMore$$.next(false);
    this.historyList$$.next([]);
  }

  groupByDate(items: ApiDeviceHistoryEntryResponseDto[]): HistoryGroupViewModel[] {
    return Array.from(
      items.reduce((map, item) => {
        const dateKey = new Date(item.eventDate).toDateString();
        if (!map.has(dateKey)) {
          map.set(dateKey, []);
        }
        map.get(dateKey)!.push(item);
        return map;
      }, new Map<string, ApiDeviceHistoryEntryResponseDto[]>()),
      ([date, items]) => ({date, items})
    );
  }
}

export interface HistoryFilter {
  deviceId?: string,
  size: number,
  page: number,
  reloadToken: number
}
